import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { api } from "../lib/api";
import { PRODUCT } from "../constants/testIds";
import ScrollReveal from "../components/ScrollReveal";
import RevealText from "../components/RevealText";
import KorvTube from "../components/KorvTube";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => {});
    api.get(`/products`).then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  if (!product) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center">
        <span className="text-[11px] uppercase tracking-editorial text-ink/55">Loading —</span>
      </div>
    );
  }

  const next = all.find((x) => x.slug !== product.slug && x.order === (product.order % all.length) + 1) || all[0];

  return (
    <div data-testid={PRODUCT.page} className="pt-24 md:pt-28">
      {/* Editorial masthead */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between text-[10px] uppercase tracking-editorial text-ink/60">
        <span>House of Korv · Volume I</span>
        <span>N° 00{product.order} · {product.category}</span>
        <Link to="/collection" className="editorial-link">Back to the Collection</Link>
      </div>
      <div className="hairline-soft" />

      {/* Hero — split canvas */}
      <section ref={heroRef} className="relative min-h-[92svh] grid grid-cols-12 gap-0">
        <div className="col-span-12 md:col-span-6 bg-bone-2 relative overflow-hidden" style={{ background: product.swatch_hex === "#e6ddcf" ? "#c9baa4" : "#d9cfc0" }}>
          <motion.div style={{ y }} className="absolute inset-0" aria-hidden>
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 55% at 50% 45%, rgba(255,255,255,0.22), transparent 65%)" }} />
          </motion.div>
          {/* Signature product mark */}
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.6, delay: 0.3, ease: [0.2, 0.7, 0.1, 1] }}
            className="absolute inset-0 flex items-center justify-center p-10"
          >
            <KorvTube
              color={product.swatch_hex}
              cap={product.swatch_hex === "#141212" || product.swatch_hex === "#c9a37a" ? "dark" : (product.swatch_hex === "#e6ddcf" ? "light" : "same")}
              sublabel={product.category.toUpperCase()}
              animate={false}
              className="max-h-[80vh] w-auto drop-shadow-[0_50px_50px_rgba(20,18,17,0.28)]"
            />
          </motion.div>
          <div className="absolute left-6 top-6 text-[10px] uppercase tracking-editorial text-ink/80">
            <span className="swatch-dot mr-3" style={{ background: product.swatch_hex }} />
            {product.category}
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 flex flex-col justify-between p-8 md:p-16 md:pl-20 relative">
          <div className="text-[10px] uppercase tracking-editorial text-ink/60 flex items-center justify-between">
            <span>korv.</span>
            <span>{product.volume}</span>
          </div>

          <div className="my-12 md:my-0">
            <p className="text-[11px] uppercase tracking-eyebrow text-plum mb-6">{product.tagline}</p>
            <RevealText
              as="h1"
              className="font-serif-display text-[18vw] md:text-[11vw] leading-[0.86] tracking-[-0.03em]"
              lines={[product.name]}
            />
            <p className="mt-10 text-[17px] leading-[1.6] max-w-[46ch] text-ink/80">
              {product.description_long}
            </p>
            <div className="mt-10 flex items-end gap-8">
              <span className="chapter-num text-4xl md:text-5xl">{product.price_display}</span>
              <button
                data-testid={PRODUCT.enquireBtn}
                onClick={() => setOpen(true)}
                className="editorial-link text-[11px] uppercase tracking-editorial pb-2 border-b border-ink"
              >
                Reserve · Enquire
              </button>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-editorial text-ink/55 flex items-center justify-between">
            <span>Fragrance-free</span>
            <span>Made in Copenhagen</span>
            <span>Weighted glass</span>
          </div>
        </div>
      </section>

      {/* Macro texture — a large product panel */}
      <section className="py-24 md:py-40 border-t border-ink/15">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <ScrollReveal className="col-span-12 md:col-span-7">
            <div className="relative aspect-[16/11] overflow-hidden" style={{ background: product.swatch_hex === "#e6ddcf" ? "#c9baa4" : "#221820" }}>
              <div className="absolute inset-0" style={{ background: "radial-gradient(60% 55% at 50% 45%, rgba(255,255,255,0.14), transparent 65%)" }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <KorvTube
                  color={product.swatch_hex}
                  cap={product.swatch_hex === "#141212" || product.swatch_hex === "#c9a37a" ? "dark" : (product.swatch_hex === "#e6ddcf" ? "light" : "same")}
                  sublabel={product.category.toUpperCase()}
                  animate={false}
                  className="h-[80%] w-auto drop-shadow-[0_30px_40px_rgba(20,18,17,0.4)]"
                />
              </div>
              <div className="absolute left-4 top-4 text-[10px] uppercase tracking-editorial text-bone/85">Macro</div>
              <div className="absolute right-4 bottom-4 text-[10px] uppercase tracking-editorial text-bone/85">{product.tagline}</div>
            </div>
            <p className="mt-3 text-[10px] uppercase tracking-editorial text-ink/60">Macro · The formulation, at rest.</p>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="col-span-12 md:col-span-4 md:col-start-9 md:pt-16">
            <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-6">On the formulation</p>
            <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] max-w-[16ch]">
              Slow-pressed. <span className="italic font-serif-italic">Considered</span>.
            </h3>
            <p className="mt-6 text-[15px] leading-[1.6] text-ink/75 max-w-[42ch]">
              A small material list, refined patiently. We revise only when improvement is measurable.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Ingredients + Ritual */}
      <section className="py-24 md:py-32 border-t border-ink/15">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <ScrollReveal className="col-span-12 md:col-span-6">
            <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-8">The Material List</p>
            <ol className="border-t border-ink/25">
              {product.ingredients.map((ing, i) => (
                <li key={i} className="grid grid-cols-12 py-5 border-b border-ink/20">
                  <span className="col-span-2 chapter-num text-plum">·0{i + 1}</span>
                  <span className="col-span-10 text-[16px] leading-[1.4]">{ing}</span>
                </li>
              ))}
            </ol>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="col-span-12 md:col-span-5 md:col-start-8">
            <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-8">The Ritual</p>
            <ol className="space-y-8">
              {product.ritual.map((step, i) => (
                <li key={i} className="border-b border-ink/20 pb-6">
                  <div className="text-[10px] uppercase tracking-editorial text-ink/60">Step {i + 1}</div>
                  <p className="font-serif-display text-2xl md:text-3xl leading-[1.15] mt-2">{step}</p>
                </li>
              ))}
            </ol>
          </ScrollReveal>
        </div>
      </section>

      {/* Next in the collection */}
      {next && (
        <section className="border-t border-ink/15 py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-6">Next in the collection</p>
              <Link to={`/collection/${next.slug}`} data-testid={PRODUCT.enquireDialog + "-next"} className="font-serif-display text-6xl md:text-8xl group inline-flex items-baseline gap-6">
                {next.name} <span className="text-[11px] tracking-editorial uppercase text-ink/60 group-hover:text-ink">→</span>
              </Link>
            </div>
            <span className="hidden md:inline text-[10px] uppercase tracking-editorial text-ink/60">{next.category}</span>
          </div>
        </section>
      )}

      {open && <EnquireDialog product={product} onClose={() => setOpen(false)} />}
    </div>
  );
}

function EnquireDialog({ product, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await api.post("/enquiries", { product_slug: product.slug, name, email, message });
      toast.success("Received. The house will be in quiet correspondence.");
      setState("ok");
      setTimeout(onClose, 900);
    } catch {
      toast.error("A quiet issue. Kindly try again.");
      setState("idle");
    }
  };

  return (
    <motion.div
      data-testid={PRODUCT.enquireDialog}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center px-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.1, 1] }}
        className="w-full max-w-xl bg-bone p-8 md:p-12 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-[11px] uppercase tracking-editorial">Close</button>
        <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-4">Reserve · Enquire</p>
        <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] mb-2">{product.name}</h3>
        <p className="text-[13px] text-ink/70 mb-8">{product.tagline} · {product.price_display}</p>
        <form onSubmit={submit} className="space-y-6">
          <input data-testid={PRODUCT.enquireName} value={name} onChange={(e)=>setName(e.target.value)} required placeholder="Your name" className="field" />
          <input data-testid={PRODUCT.enquireEmail} type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required placeholder="Your address" className="field" />
          <textarea data-testid={PRODUCT.enquireMessage} value={message} onChange={(e)=>setMessage(e.target.value)} placeholder="A note (optional)" rows={3} className="field resize-none" />
          <button data-testid={PRODUCT.enquireSubmit} disabled={state==="loading"} className="mt-2 text-[11px] uppercase tracking-editorial pb-2 border-b border-ink">
            {state === "loading" ? "Sending —" : "Send Enquiry"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}
